import { useMutation } from "@tanstack/react-query";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  type ButtonProps,
} from "ui";
import { useToast } from "ui";

import { useExercisesRepository } from "src/contexts/ExercisesRepositoryContext";
import { type ExerciseListItem } from "src/repositories/exercises/ExercisesRepository";
import { useExerciseCategories } from "src/state/exerciseCategories";

import { ExerciseForm, type CreateExerciseValues } from "./ExerciseForm";

export interface CreateExerciseDialogProps {
  onCreateExercise: (exercise: ExerciseListItem) => void;
  buttonProps?: ButtonProps;
}

export function CreateExerciseDialog(props: CreateExerciseDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const categories = useExerciseCategories();
  const exercisesRepository = useExercisesRepository();

  const createExerciseMutation = useMutation({
    mutationFn: async (values: CreateExerciseValues) => {
      const category = categories.find((category) => {
        return category.name.toLowerCase() === values.category;
      })!;
      return exercisesRepository.createExercise({
        name: values.name,
        description: values.description,
        videoURL: values.videoURL,
        categoryId: category.id,
        primaryMusclesIds: [values.primaryMusclesId],
        secondaryMusclesIds: values.secondaryMusclesIds,
        movementPatternIds: values.movementPatternIds,
      });
    },
    onSuccess: props.onCreateExercise,
    onError: () => {
      toast.toast({
        title: t("createExercise.creationError.title"),
        description: t("createExercise.creationError.description"),
        variant: "destructive",
      });
    },
  });

  async function handleSubmit(values: CreateExerciseValues) {
    if (createExerciseMutation.isPending) {
      return;
    }

    createExerciseMutation.mutate(values);
  }

  const formId = "createExerciseForm";

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button {...props.buttonProps} type="button">
          {props.buttonProps?.size === "icon" ? (
            <Icon name="tabler-plus" className="h-4 w-4" />
          ) : (
            <div>{t("createExercise.trigger")}</div>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("createExercise.title")}</DialogTitle>
          <DialogDescription>
            {t("createExercise.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[calc(100svh_-_46px_-_8rem_-_40px)] -m-1 p-1">
          <ExerciseForm
            formId={formId}
            onSubmit={handleSubmit}
            defaultValues={{
              name: "",
              description: "",
              category: "",
              primaryMusclesId: "",
              secondaryMusclesIds: [],
              movementPatternIds: [],
            }}
          />
        </div>
        <DialogFooter>
          <Button
            form={formId}
            loading={createExerciseMutation.isPending}
            type={"submit"}
          >
            {t("createExercise.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
